import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HotelIcon from "@mui/icons-material/Hotel";
import ChairIcon from "@mui/icons-material/Chair";
import api from "../services/api";
import StudentBookingHistory from "../pages/StudentBookingHistory";

const paymentChip = (status) => {
  if (status === "paid") return { label: "Paid", color: "success" };
  if (status === "unpaid") return { label: "Unpaid", color: "error" };
  return { label: "No Invoice", color: "default" };
};

const GlobalStudentSearch = () => {
  const [query, setQuery] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [module, setModule] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openResults, setOpenResults] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpenResults(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenResults(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    const hasPurposefulFilter = paymentStatus !== "all";
    if (!trimmed && !hasPurposefulFilter) {
      setResults([]);
      setOpenResults(false);
      setLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setOpenResults(true);
      try {
        const params = { module };
        if (trimmed) params.search = trimmed;
        if (paymentStatus !== "all") params.payment_status = paymentStatus;
        const response = await api.get("/users/students/", { params });
        setResults(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Global student search failed", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query, paymentStatus, module]);

  const selectStudent = (student) => {
    setSelectedStudentId(student.id);
    setOpenResults(false);
  };

  return (
    <>
      <Box ref={rootRef} sx={{ position: "relative", width: { xs: "100%", lg: 650 }, maxWidth: "100%" }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center" }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => (results.length || loading) && setOpenResults(true)}
            placeholder="Search student by name, username, ID..."
            size="small"
            fullWidth
            inputProps={{ "aria-label": "Search students" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? <CircularProgress size={18} /> : null,
            }}
            sx={{ minWidth: { xs: "100%", sm: 260 }, bgcolor: "white" }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 4px)", sm: 115 }, bgcolor: "white" }}>
            <Select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} displayEmpty>
              <MenuItem value="all">All Payments</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: "calc(50% - 4px)", sm: 100 }, bgcolor: "white" }}>
            <Select value={module} onChange={(e) => setModule(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="hostel">Hostel</MenuItem>
              <MenuItem value="library">Library</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {openResults && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              right: 0,
              zIndex: 50,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              boxShadow: "0 14px 35px rgba(15,23,42,.18)",
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {loading ? (
              <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={28} /></Box>
            ) : results.length === 0 ? (
              <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No students found</Typography>
            ) : (
              results.map((student) => {
                const displayedPaymentStatus =
                  module === "hostel"
                    ? student.hostel_payment_status
                    : module === "library"
                    ? student.library_payment_status
                    : student.payment_status;

                const chip = paymentChip(displayedPaymentStatus);
                const fullName = [student.first_name, student.middle_name, student.last_name].filter(Boolean).join(" ") || student.username;
                return (
                  <Box
                    key={student.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectStudent(student)}
                    onKeyDown={(e) => e.key === "Enter" && selectStudent(student)}
                    sx={{ p: 1.5, display: "flex", gap: 1.5, cursor: "pointer", borderBottom: "1px solid #f1f5f9", "&:hover": { bgcolor: "#f8fafc" } }}
                  >
                    <Avatar src={student.profile_photo || undefined}>{fullName.charAt(0).toUpperCase()}</Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography fontWeight={700} noWrap>{fullName}</Typography>
                        <Chip label={chip.label} color={chip.color} size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        @{student.username} • Student ID: {student.id} {student.phone_number ? `• ${student.phone_number}` : ""}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, mt: .5, flexWrap: "wrap" }}>
                        {module !== "library" && student.hostel_booking_id && (
                          <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: .4 }}>
                            <HotelIcon sx={{ fontSize: 15 }} /> Hostel: {student.hostel_room || "—"}{student.hostel_bed ? ` / Bed ${student.hostel_bed}` : ""}
                          </Typography>
                        )}
                        {module !== "hostel" && student.library_booking_id && (
                          <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: .4 }}>
                            <ChairIcon sx={{ fontSize: 15 }} /> Library: Seat {student.library_seat || "—"}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>

      <StudentBookingHistory
        open={Boolean(selectedStudentId)}
        onClose={() => setSelectedStudentId(null)}
        studentId={selectedStudentId}
      />
    </>
  );
};

export default GlobalStudentSearch;
